import './less/index.less';

import { message } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import * as noticeApi from '@/services/notifications';
import { INoticeMessage, INoticeModule } from '@/services/notifications/types';

import Header from './components/Header';
const Messages = React.lazy(() => import('./components/Message'));
const Nav = React.lazy(() => import('./components/Nav'));
import { navigateToAPP, NOTIFICATION_PAGE_DEEP_LINK } from '@/utils/deeplink';
import isApp from '@/utils/ua';

import { defaultModule, unread } from './helper';

const Notifications = () => {
  const [search] = useSearchParams();
  const { t: $t } = useTranslation(['notifications']);
  const searchModuleId = +(search.get('moduleId') || 0) || defaultModule.id;
  const [curModule, setCurModule] = useState<INoticeModule>({
    ...defaultModule,
    id: searchModuleId,
  });

  const [moduleList, setModuleList] = useState<Array<INoticeModule>>([]);
  const [moduleLoading, setModuleLoading] = useState(true);
  const [messageLoading, setMessageLoading] = useState(true);
  const [messageList, setMessageList] = useState<Array<INoticeMessage>>([]);
  const [pager, setPager] = useState({
    curPage: 1,
    total: 0,
  });

  useEffect(() => {
    if (isApp()) {
      navigateToAPP(NOTIFICATION_PAGE_DEEP_LINK, true);
    }
  }, []);

  const getMessageList = (
    module_id: number,
    pageNumber: number,
    h5Load = false,
  ) => {
    setMessageLoading(true);
    noticeApi
      .getMessageList({
        module_id,
        page_size: 10,
        page_num: pageNumber,
        is_show_unread: false,
      })
      .then((res) => {
        const { data, page_num, count } = res || {
          data: [],
          page_num: 0,
          count: 0,
        };
        if (h5Load) {
          setMessageList((list) => {
            list.push(...(data?.filter?.((item) => item) || []));
            return list;
          });
        } else {
          // eslint-disable-next-line no-multi-assign
          document.body.scrollTop = document.documentElement.scrollTop = 0;
          setMessageList(data?.filter?.((item) => item) || []);
        }
        setPager({
          curPage: page_num,
          total: count,
        });
      })
      .finally(() => {
        setMessageLoading(false);
      });
  };

  const getModuleList = () => {
    setModuleLoading(true);
    noticeApi
      .getModuleList()
      .then((result) => {
        setModuleList(result || []);
        const module =
          result.find((item) => {
            return item.id === curModule.id;
          }) || defaultModule;
        setCurModule(module);
        if (searchModuleId !== module.id) {
          getMessageList(module.id, 1);
        }
      })
      .catch((error) => {
        let errorMsg = '';
        if (error?.ret_code && error?.ret_code !== -200) {
          errorMsg = $t(error.ret_code, { ns: 'inverse-error-code' });
          if (errorMsg === `${error.ret_code}`) {
            errorMsg = error.ret_msg;
          }
        }
        message.error(errorMsg || 'Network error');
      })
      .finally(() => {
        setModuleLoading(false);
      });
  };

  useEffect(() => {
    getModuleList();
    getMessageList(searchModuleId, 1);
    window.clearNotificationUnread = () => {
      setUnreadModule({ id: 0, messageId: -1, count: -1 });
    };
    if (!window.USNotice) {
      window.USNotice = {};
    }
    window.USNotice.updateListUnreadNumber = (
      moduleId: number,
      messageId: number,
    ) => {
      setUnreadModule({ id: moduleId, messageId: messageId, count: 1 });
    };
    window.USNotice.clearUnread = () => {
      setUnreadModule({ id: 0, messageId: -1, count: -1 });
    };
  }, []);

  const [unreadModule, setUnreadModule] = useState({
    id: -1,
    messageId: -1,
    count: -1,
  });

  useEffect(() => {
    if (unreadModule.id >= 0) {
      const list = unread(moduleList, unreadModule.id, unreadModule.count);
      setModuleList(list || []);

      if (unreadModule.count === -1) {
        setMessageList((list) => {
          return list.map((item) => {
            return { ...item, is_read: 2 };
          });
        });
      } else {
        setMessageList((list) => {
          return list.map((item) => {
            if (item.id === unreadModule.messageId) {
              item.is_read = 2;
            }
            return item;
          });
        });
      }
    }
  }, [unreadModule]);

  const changeModule = (module: INoticeModule | undefined) => {
    const curModule = module || defaultModule;
    setCurModule(curModule);
    getMessageList(curModule.id, 1);
  };

  return (
    <main className="notice-warp">
      <Header
        moduleId={curModule.id}
        onReadAll={(moduleId) => {
          setUnreadModule({ id: moduleId, messageId: -1, count: -1 });
          window?.USNotice?.getIsUnread?.();
        }}
      />
      <div className="notice-content">
        <div className="notice-content__left">
          <React.Suspense fallback="">
            <Nav
              loading={moduleLoading}
              curModuleId={curModule.id}
              moduleList={moduleList}
              onChangeModule={changeModule}
            />
          </React.Suspense>
        </div>
        <div className="notice-content__right">
          <React.Suspense fallback="">
            <Messages
              moduleList={moduleList}
              messageList={messageList}
              messageLoading={messageLoading}
              pager={pager}
              onUnread={(moduleId, messageId) => {
                setUnreadModule({ id: moduleId, messageId, count: 1 });
                window?.USNotice?.getIsUnread?.();
              }}
              onTurnPage={(pageNum: number, h5Load = false) => {
                if (!h5Load) {
                  window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth',
                  });
                }
                getMessageList(curModule.id, pageNum, h5Load);
              }}
            />
          </React.Suspense>
        </div>
      </div>
    </main>
  );
};

export default Notifications;
